import React from "react";
import { Box, Link as MuiLink, Paper, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

export const CDR_FAQS = [
  {
    question: "What is a CDR analyzer?",
    answer: "A CDR analyzer is a tool that reads authorized Call Detail Records and organizes call, SMS, contact, location, and device information into searchable summaries. It helps analysts validate files and investigate patterns without treating a pattern as proof by itself.",
  },
  {
    question: "Can I analyze CDR data online for free?",
    answer: "Yes. Cyberpunk provides a free browser-based CDR analysis workspace for supported Excel and CSV files. The tool validates common Jazz, Zong, Ufone, and Telenor layouts, then creates contact, location, IMEI, timeline, and network views.",
  },
  {
    question: "How do I check a CDR file online?",
    answer: "Open the CDR Analyzer, upload an authorized Excel or CSV export, review the detected operator and column validation, and let the tool generate the analysis. Use the filters and export controls to document the exact rows included.",
  },
  {
    question: "Which Pakistan telecom CDR formats are supported?",
    answer: "The analyzer supports common CDR layouts for Jazz, Zong, Ufone, and Telenor. It checks exact headers and can skip leading summary rows in supported exports, including Zong files with customer-information preambles.",
  },
  {
    question: "Does CDR analysis prove that someone committed a crime?",
    answer: "No. CDR analysis produces evidence-oriented summaries and investigative leads. Results should be checked against the original records, lawful authorization, complaint chronology, and independent evidence before any conclusion is made.",
  },
];

export function CdrAeoContent() {
  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", mt: 3, pb: 3 }}>
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Typography variant="h5" component="h2" gutterBottom>CDR Analysis Online: Quick Answer</Typography>
        <Typography color="text.secondary" paragraph>
          A CDR analyzer reviews authorized Call Detail Records and turns telecom exports into clear contact, location, device, timeline, and activity summaries. Cyberpunk is a free browser-based CDR analysis tool for supported Pakistan operator Excel and CSV formats, including Jazz, Zong, Ufone, and Telenor.
        </Typography>
        <Typography color="text.secondary">
          Start with the <MuiLink component={RouterLink} to="/analysis">CDR Analyzer</MuiLink>, read the <MuiLink component={RouterLink} to="/blog">CDR analysis guides</MuiLink>, and review the <MuiLink component={RouterLink} to="/privacy-policy">Privacy Policy</MuiLink> before uploading authorized data.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Typography variant="h5" component="h2" gutterBottom>How to Check CDR Data Online</Typography>
        <Stack component="ol" spacing={1} sx={{ pl: 3, color: "text.secondary" }}>
          <li>Upload an authorized `.xlsx`, `.xls`, `.xlsm`, `.xlsb`, or `.csv` CDR file.</li>
          <li>Review the detected operator, exact headers, skipped preamble rows, and data-quality warnings.</li>
          <li>Inspect contacts, network graph, locations, IMEI usage, time windows, and anomalies.</li>
          <li>Export the filtered results to Excel or PDF with row and filter metadata.</li>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" component="h2" gutterBottom>CDR Analyzer Questions</Typography>
        <Stack spacing={2}>
          {CDR_FAQS.map((faq) => (
            <Box key={faq.question}>
              <Typography variant="h6" component="h3" sx={{ fontSize: { xs: "1rem", sm: "1.1rem" } }}>{faq.question}</Typography>
              <Typography color="text.secondary">{faq.answer}</Typography>
            </Box>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}
